import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc, onSnapshot, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import SystemPrompts from './SystemPrompts';

const CATEGORIES = ['Blogs', 'Social', 'Google Business Profile', 'Video', 'Miscellaneous'];

export default function Prompts() {
  const [prompts, setPrompts] = useState([]);
  const [form, setForm] = useState({ title: '', instructions: '', categories: [], prompt: '' });
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'prompts'), snap => {
      setPrompts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, []);

  const toggleCat = (cat) => {
    setForm(f => ({
      ...f,
      categories: f.categories.includes(cat)
        ? f.categories.filter(c => c !== cat)
        : [...f.categories, cat]
    }));
  };

  const handleSave = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    await addDoc(collection(db, 'prompts'), {
      title: form.title,
      instructions: form.instructions,
      categories: form.categories,
      prompt: form.prompt,
      content: form.prompt,
      createdAt: serverTimestamp(),
    });
    setForm({ title: '', instructions: '', categories: [], prompt: '' });
    setSaving(false);
  };

  const deletePrompt = async (id) => {
    if (window.confirm('Delete this prompt?')) {
      await deleteDoc(doc(db, 'prompts', id));
    }
  };

  const getPromptsByCategory = (cat) => {
    return prompts.filter(p => {
      const cats = p.categories || (p.category ? [p.category] : []);
      return cats.includes(cat);
    });
  };

  const uncategorized = prompts.filter(p => {
    const cats = p.categories || (p.category ? [p.category] : []);
    return cats.length === 0 || !cats.some(c => CATEGORIES.includes(c));
  });

  return (
    <div>
      <div className="prompts-layout">
      <div className="card" style={{ height: 'fit-content' }}>
        <div className="card-title">Add New Prompt</div>
        <div className="form-field">
          <label>Title</label>
          <input type="text" placeholder="e.g. Social Media Post Idea Generator" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
        </div>
        <div className="form-field">
          <label>Instructions</label>
          <textarea placeholder="Add notes on how to use this prompt..." value={form.instructions} onChange={e => setForm(f => ({ ...f, instructions: e.target.value }))} style={{ minHeight: 70 }} />
        </div>
        <div className="form-field">
          <label>Categories</label>
          {CATEGORIES.map(cat => (
            <label key={cat} className="checkbox-row">
              <input type="checkbox" checked={form.categories.includes(cat)} onChange={() => toggleCat(cat)} />
              <span>{cat}</span>
            </label>
          ))}
        </div>
        <div className="form-field">
          <label>Prompt</label>
          <textarea placeholder="Enter the prompt text here..." value={form.prompt} onChange={e => setForm(f => ({ ...f, prompt: e.target.value }))} style={{ minHeight: 100 }} />
        </div>
        <button className="btn btn-primary" style={{ width: '100%' }} onClick={handleSave} disabled={saving || !form.title.trim()}>
          {saving ? 'Saving...' : 'Save Prompt'}
        </button>
      </div>

      <div className="card">
        <div className="card-title">Saved Prompts</div>
        {CATEGORIES.map(cat => {
          const catPrompts = getPromptsByCategory(cat);
          if (catPrompts.length === 0) return null;
          return (
            <div key={cat} className="prompt-category">
              <div className="prompt-category-title">{cat}</div>
              {catPrompts.map(p => (
                <div key={p.id}>
                  <div className="prompt-list-item" onClick={() => setExpanded(expanded === p.id ? null : p.id)}>
                    <span className="prompt-drag">⠿</span>
                    <span className="prompt-name">{p.title || p.name}</span>
                    <button className="btn btn-sm btn-danger" onClick={(e) => { e.stopPropagation(); deletePrompt(p.id); }}>🗑</button>
                  </div>
                  {expanded === p.id && (
                    <div style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', borderTop: 'none', borderRadius: '0 0 6px 6px', padding: 12, marginBottom: 4 }}>
                      {p.instructions && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}><strong>Instructions:</strong> {p.instructions}</div>}
                      {(p.prompt || p.content || p.text) && (
                        <div style={{ fontSize: 12, color: 'var(--text-primary)', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                          {p.prompt || p.content || p.text}
                        </div>
                      )}
                      <button className="btn btn-sm btn-secondary" style={{ marginTop: 8 }} onClick={() => { navigator.clipboard.writeText(p.prompt || p.content || p.text || ''); }}>
                        Copy Prompt
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          );
        })}
        {uncategorized.length > 0 && (
          <div className="prompt-category">
            <div className="prompt-category-title">Other</div>
            {uncategorized.map(p => (
              <div key={p.id} className="prompt-list-item" onClick={() => setExpanded(expanded === p.id ? null : p.id)}>
                <span className="prompt-drag">⠿</span>
                <span className="prompt-name">{p.title || p.name}</span>
                <button className="btn btn-sm btn-danger" onClick={(e) => { e.stopPropagation(); deletePrompt(p.id); }}>🗑</button>
              </div>
            ))}
          </div>
        )}
        {prompts.length === 0 && <div className="empty-state">No prompts saved yet</div>}
      </div>
      </div>
      <SystemPrompts />
    </div>
  );
}
